import { FocusMonitor } from "@angular/cdk/a11y";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, Optional, Output, Self } from "@angular/core";
import { ControlValueAccessor, NgControl, Validators } from "@angular/forms";
import { MatFormFieldControl } from "@angular/material/form-field";
import { Subject } from "rxjs";

@Component({template: ''})
export class MatFormFieldBaseComponent<T> implements MatFormFieldControl<T | T[]>, ControlValueAccessor, OnDestroy {

	@Input() id: string;
	
	@Input() placeholder: string;
	
	_value: T | T[];
	_values: T[];
	@Input()
	get value(): T | T[] { return this._value; }
	set value(value: T | T[]) { 
		this._value = value; 
		this._values = (value instanceof Array ? value : (value ? [value] : []));
        this._onChange(value);
		this.stateChanges.next();
	}

	_multiple: boolean = true;
	@Input()
	get multiple() { return this._multiple; }
	set multiple(value: any) { this._multiple = coerceBooleanProperty(value); this.stateChanges.next(); }

	_required: boolean = false;
	@Input()
	get required() { return this._required || this.ngControl?.control.hasValidator(Validators.required); }
	set required(value: any) { this._required = coerceBooleanProperty(value); this.stateChanges.next(); }

	_disabled: boolean = false;
	@Input()
	get disabled() { return this._disabled; }
	set disabled(value: any) { this._disabled = coerceBooleanProperty(value); this.stateChanges.next(); }

	get shouldLabelFloat() { return this.focused || !this.empty; }
	get errorState(): boolean { return this.ngControl?.control.touched && this.ngControl?.control.invalid; }

	get isMatFormField (): boolean { return !!this.elementRef.nativeElement.closest('.mat-form-field'); }

	@Output() valueChange: EventEmitter<T | T[]> = new EventEmitter();

	focused: boolean = false;
	stateChanges: Subject<void> = new Subject<void>();

	constructor(@Optional() @Self() public ngControl: NgControl,
				public focusMonitor: FocusMonitor,
				public elementRef: ElementRef<HTMLElement>) {

		// Replace the provider from above with this.
		if (this.ngControl !== null) {
			// Setting the value accessor directly (instead of using
			// the providers) to avoid running into a circular import.
			this.ngControl.valueAccessor = this;
		}

		this.focusMonitor.monitor(this.elementRef, true).subscribe(origin => {
			this.focused = !!origin;
			this.stateChanges.next();
		});
	}

	get empty(): boolean {
		return (this._multiple ? !(this._value as T[])?.length : !this._value);
	}

	private _onChange = (_: T | T[]) => {};
	private _onTouched = () => {};

	ngOnDestroy () {
		this.focusMonitor.stopMonitoring(this.elementRef);
		this.stateChanges.complete();
	}

	onChange (): void {
		this._onTouched();
		this._onChange(this.value);
		this.stateChanges.next();
		this.valueChange.emit(this.value);
	}

	setDisabledState(isDisabled: boolean) { this.disabled = isDisabled; }

	setDescribedByIds(): void {}
	onContainerClick(_?: Event): void { this.elementRef.nativeElement.focus(); }

	writeValue(value: T | T[]): void { this.value = value; this._onChange(value); }

	registerOnChange(fn: any): void { this._onChange = fn }
	registerOnTouched(fn: any): void { this._onTouched = fn }
}